import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Car,
  ExternalLink,
  FileText,
  IdCard,
  Loader2,
  Lock,
  MapPin,
  Pencil,
  RefreshCw,
  Users,
} from "lucide-react";
import EmployeeAssignmentsDrawer from "@/components/EmployeeAssignmentsDrawer";
import { AccountPolicyBanner, usePolicyBanner } from "@/components/AccountPolicyBanner";
import { useOwnerSession } from "@/hooks/useOwnerSession";
import { getVehiclePictogramSrc } from "@/lib/vehicleCategoryIcons";
import {
  AmpelStatus,
  EmployeeForm,
  LANGUAGE_OPTIONS,
  PinRevealDialog,
  emptyAssignmentStats,
  groupAssignmentStats,
} from "./Mitarbeiter";

/**
 * CLARTEXT · UI-REDESIGN Master-Detail (Aug 2026) — Mitarbeiter-Detailseite.
 *
 * Tabs: Stammdaten / PIN / Einsatzbereiche / Unterweisungen / Führerschein.
 * - Unterweisungen: 1:1 die bestehende Funktionalität der alten Listenzeile
 *   (fünf klickbare Kategorien -> EmployeeAssignmentsDrawer, unverändert).
 * - Führerschein-Tab: NUR sichtbar bei aktivem Modul (Block-H-Gate,
 *   api.getModuleAddonStatus, fail-closed). Nur Fahrer-Stammdaten + kurzer
 *   Status — der Freigabe-Workflow bleibt exklusiv auf /unternehmer/fuehrerschein.
 * - Bearbeitung: bestehender EmployeeForm-Dialog (kein Inline-Edit).
 */

// Vertrags-Konstante für Tests (Muster __NAV_ITEMS_FOR_TESTS__).
export const __DETAIL_TABS_FOR_TESTS__ = [
  { key: "stammdaten", label: "Stammdaten" },
  { key: "pin", label: "PIN" },
  { key: "einsatzbereiche", label: "Einsatzbereiche" },
  { key: "unterweisungen", label: "Unterweisungen" },
  { key: "fuehrerschein", label: "Führerschein", featureModule: "fuehrerschein" },
];

const STAT_META = [
  {
    key: "handlungsbedarf",
    testKey: "handlungsbedarf",
    label: "Handlungsbedarf",
    title: "Aktueller Handlungsbedarf",
    hover: "hover:bg-amber-50 focus:ring-amber-300",
    color: (v) => (v > 0 ? "text-amber-700" : "text-slate-900"),
  },
  {
    key: "bald_faellig",
    testKey: "bald-faellig",
    label: "Bald fällig",
    title: "Bald fällige Aufgaben (≤ 30 Tage)",
    hover: "hover:bg-sky-50 focus:ring-sky-300",
    color: () => "text-sky-700",
  },
  {
    key: "ueberfaellig",
    testKey: "ueberfaellig",
    label: "Überfällig",
    title: "Überfällige Aufgaben anzeigen",
    hover: "hover:bg-rose-50 focus:ring-rose-300",
    color: () => "text-red-700",
  },
  {
    key: "geplant",
    testKey: "geplant",
    label: "Geplant",
    title: "Geplante Wiederholungen (kein aktueller Handlungsbedarf)",
    hover: "hover:bg-slate-100 focus:ring-slate-300",
    color: () => "text-slate-400",
  },
  {
    key: "erledigt",
    testKey: "erledigt",
    label: "Erledigt",
    title: "Erledigte Aufgaben anzeigen",
    hover: "hover:bg-emerald-50 focus:ring-emerald-300",
    color: () => "text-emerald-700",
  },
];

function InfoRow({ label, children, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="text-sm text-slate-500 shrink-0 w-36">{label}</div>
      <div className="text-sm text-slate-900 text-right min-w-0" data-testid={testId}>
        {children}
      </div>
    </div>
  );
}

// UI-REDESIGN Ergänzung (Aug 2026, Punkt 6): Wiederverwendung des bereits auf
// der Führerschein-Owner-Seite eingesetzten Blob-Save-Musters. Kein neuer
// Endpoint, keine Backend-Änderung — nur Client-Download der bestehenden
// Bytes (PDF-Nachweis + WORM-Rohdatei) für den letzten entschiedenen Record
// des jeweiligen Mitarbeiters.
function saveBlobAs(blob, fallbackName, headers) {
  let filename = fallbackName;
  const disposition =
    (headers && (headers["content-disposition"] || headers["Content-Disposition"])) || "";
  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  if (m && m[1]) filename = m[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function MitarbeiterDetail() {
  const { id } = useParams();
  const { session } = useOwnerSession();
  const [employee, setEmployee] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [einsatzbereiche, setEinsatzbereiche] = useState([]);
  const [vehicleCategories, setVehicleCategories] = useState([]);
  const [stats, setStats] = useState(emptyAssignmentStats());
  // Block-H-Gate (fail-closed): Tab erst nach positiver Bestätigung sichtbar.
  const [fskModuleActive, setFskModuleActive] = useState(false);
  const [hasOpenFskRecord, setHasOpenFskRecord] = useState(false);
  // UI-REDESIGN Ergänzung (Aug 2026, Punkt 6): letzter abgeschlossener
  // Führerschein-Record des Mitarbeiters für PDF/WORM-Download im Tab.
  // Rein clientseitiger Filter über den existierenden Decided-Endpoint —
  // keine neuen Daten, kein Foto (BE-003 bleibt gelöscht).
  const [latestDecidedRecord, setLatestDecidedRecord] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pinReveal, setPinReveal] = useState({ open: false, pin: "", name: "" });
  const [drawerState, setDrawerState] = useState({
    open: false,
    category: "handlungsbedarf",
  });
  const { policyError, handlePolicyError, clearPolicyError } = usePolicyBanner();

  const reload = useCallback(() => {
    return Promise.all([
      api.getEmployee(id),
      api.listAssignments({ employee_id: id }),
    ])
      .then(([emp, asns]) => {
        setEmployee(emp);
        setStats(groupAssignmentStats(asns)[emp.id] || emptyAssignmentStats());
      })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error("Mitarbeiter konnte nicht geladen werden.");
      });
  }, [id]);

  useEffect(() => {
    reload();
    api.listEinsatzbereiche(true).then(setEinsatzbereiche).catch(() => {});
    api
      .getVehicleCategories()
      .then((cats) => setVehicleCategories(Array.isArray(cats) ? cats : []))
      .catch(() => setVehicleCategories([]));
    // Block-H-Gate: identischer READ-ONLY-Call wie im Sidebar-Gate
    // (UnternehmerLayout.jsx) — fail-closed, Fehler => Tab bleibt verborgen.
    api
      .getModuleAddonStatus("fuehrerschein")
      .then((s) => setFskModuleActive(!!s?.module_active))
      .catch(() => {});
  }, [id, reload]);

  // Kurzer FSK-Status: gibt es eine offene Prüfung für diesen Mitarbeiter?
  useEffect(() => {
    if (!fskModuleActive) return;
    api
      .listOpenFskRecords()
      .then((records) =>
        setHasOpenFskRecord(
          (records || []).some((r) => (r.employee?.id || r.employee_id) === id)
        )
      )
      .catch(() => {});
  }, [fskModuleActive, id]);

  // UI-REDESIGN Ergänzung (Aug 2026, Punkt 6): letzter entschiedener Record.
  // Nutzt den existierenden Owner-Endpoint `/fuehrerschein/records/decided`
  // (kein neuer Endpoint) und filtert clientseitig auf den Mitarbeiter.
  // Sortierung: neuester zuerst — die Payload liefert bereits eine
  // Downloads-Zusammenfassung, aber wir brauchen nur `id` + `decided_at`.
  useEffect(() => {
    if (!fskModuleActive) {
      setLatestDecidedRecord(null);
      return;
    }
    api
      .listDecidedFskRecords()
      .then((records) => {
        const own = (records || []).filter(
          (r) => (r.employee?.id || r.employee_id) === id
        );
        own.sort((a, b) => {
          const ad = String(a.decided_at || a.mandant_decision_at || "");
          const bd = String(b.decided_at || b.mandant_decision_at || "");
          return bd.localeCompare(ad);
        });
        setLatestDecidedRecord(own[0] || null);
      })
      .catch(() => setLatestDecidedRecord(null));
  }, [fskModuleActive, id]);

  const onArchiveToggle = async () => {
    try {
      await api.archiveEmployee(employee.id, !employee.archived);
      toast.success(
        employee.archived ? "Mitarbeiter aktiviert." : "Mitarbeiter archiviert."
      );
      reload();
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error("Aktion fehlgeschlagen.");
      }
    }
  };

  // Phase 6 / Go-Live P0 – PIN-Verwaltung (1:1 aus der alten Listenansicht).
  const onRegeneratePin = async () => {
    if (
      !window.confirm(
        `Neuen PIN für ${employee.first_name} ${employee.last_name} erzeugen?\n\nAlle bestehenden Anmeldungen werden sofort beendet.`
      )
    )
      return;
    try {
      const res = await api.regenerateEmployeePin(employee.id);
      setPinReveal({
        open: true,
        pin: res.pin,
        name: `${employee.first_name} ${employee.last_name}`,
      });
      reload();
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "PIN-Reset fehlgeschlagen.");
      }
    }
  };

  const languageLabel = useMemo(() => {
    const opt = LANGUAGE_OPTIONS.find(
      (o) => o.value === (employee?.preferred_language || "de")
    );
    return opt ? opt.label : employee?.preferred_language || "Deutsch";
  }, [employee]);

  const visibleTabs = __DETAIL_TABS_FOR_TESTS__.filter(
    (t) => !t.featureModule || (t.featureModule === "fuehrerschein" && fskModuleActive)
  );

  if (notFound) {
    return (
      <div className="max-w-4xl mx-auto py-12" data-testid="mitarbeiter-detail-not-found">
        <Card className="bg-white border-slate-200 p-8 text-center">
          <Users className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            Mitarbeiter nicht gefunden.
          </h2>
          <Link
            to="/unternehmer/mitarbeiter"
            className="inline-flex items-center gap-2 mt-4 text-sm text-slate-600 hover:text-slate-900"
            data-testid="mitarbeiter-detail-back-notfound"
          >
            <ArrowLeft className="w-4 h-4" /> Zur Mitarbeiterliste
          </Link>
        </Card>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="max-w-4xl mx-auto py-16 text-center text-slate-500" data-testid="mitarbeiter-detail-loading">
        <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
        <p className="text-sm">Wird geladen…</p>
      </div>
    );
  }

  const ebs = employee.einsatzbereiche || [];

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="mitarbeiter-detail-page">
      <Link
        to="/unternehmer/mitarbeiter"
        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
        data-testid="mitarbeiter-detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> Zur Mitarbeiterliste
      </Link>

      {policyError && (
        <AccountPolicyBanner error={policyError} onDismiss={clearPolicyError} />
      )}

      {/* Kopf: Avatar, Name, Status, Ampel, Aktionen */}
      <Card className="bg-white border border-slate-200 shadow-sm p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            {employee.photo_url ? (
              <img
                src={employee.photo_url}
                alt=""
                className="w-14 h-14 rounded-full object-cover bg-slate-100"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-medium">
                {(employee.first_name?.[0] || "") + (employee.last_name?.[0] || "")}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1
                  className="font-heading text-2xl font-bold text-slate-900"
                  data-testid="mitarbeiter-detail-name"
                >
                  {employee.first_name} {employee.last_name}
                </h1>
                {employee.is_demo && (
                  <Badge className="bg-sky-100 hover:bg-sky-100 text-sky-700 text-[10px] uppercase tracking-wider">
                    Demo
                  </Badge>
                )}
                {employee.archived && (
                  <Badge
                    className="bg-slate-100 hover:bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider"
                    data-testid="mitarbeiter-detail-archived-badge"
                  >
                    Archiviert
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <AmpelStatus stats={stats} employeeId={employee.id} />
            <Button
              variant="outline"
              onClick={() => setEditOpen(true)}
              data-testid="mitarbeiter-detail-edit"
              className="h-9"
            >
              <Pencil className="w-4 h-4 mr-1.5" /> Bearbeiten
            </Button>
            <Button
              variant="outline"
              onClick={onArchiveToggle}
              data-testid="mitarbeiter-detail-archive"
              className="h-9"
            >
              {employee.archived ? (
                <>
                  <ArchiveRestore className="w-4 h-4 mr-1.5" /> Reaktivieren
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4 mr-1.5" /> Archivieren
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="stammdaten" data-testid="mitarbeiter-detail-tabs">
        <TabsList className="flex-wrap h-auto">
          {visibleTabs.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`mitarbeiter-tab-${t.key}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Stammdaten */}
        <TabsContent value="stammdaten">
          <Card className="bg-white border border-slate-200 shadow-sm divide-y divide-slate-100">
            <InfoRow label="Vorname" testId="detail-stammdaten-first">
              {employee.first_name}
            </InfoRow>
            <InfoRow label="Nachname" testId="detail-stammdaten-last">
              {employee.last_name}
            </InfoRow>
            <InfoRow label="Sprache" testId="detail-stammdaten-language">
              {languageLabel}
            </InfoRow>
            <InfoRow label="Foto" testId="detail-stammdaten-photo">
              {employee.photo_url ? (
                <span className="break-all">{employee.photo_url}</span>
              ) : (
                "—"
              )}
            </InfoRow>
            <InfoRow label="Status" testId="detail-stammdaten-status">
              {employee.archived ? "Archiviert" : "Aktiv"}
            </InfoRow>
          </Card>
        </TabsContent>

        {/* PIN */}
        <TabsContent value="pin">
          <Card className="bg-white border border-slate-200 shadow-sm p-6 space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                  PIN
                </div>
                <div
                  className="font-heading text-sm font-semibold text-slate-700 flex items-center gap-1.5 mt-1"
                  data-testid="detail-pin-status"
                >
                  <Lock className="w-3.5 h-3.5 text-slate-400" />
                  {employee.has_pin ? "gesetzt" : "—"}
                </div>
              </div>
              {!employee.archived && (
                <Button
                  variant="outline"
                  onClick={onRegeneratePin}
                  data-testid="detail-pin-regen"
                  className="h-9 gap-1.5"
                >
                  <RefreshCw className="w-4 h-4" />
                  Neu &amp; anzeigen
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-500">
              PINs werden verschlüsselt gespeichert und sind nicht im Klartext
              abrufbar. „Neu &amp; anzeigen&ldquo; erzeugt einen neuen PIN, der
              einmalig angezeigt wird — alle bestehenden Anmeldungen des
              Mitarbeiters werden dabei beendet.
            </p>
            {employee.archived && (
              <p
                className="text-xs text-slate-500 italic"
                data-testid="detail-pin-archived-hint"
              >
                Mitarbeiter ist archiviert – PIN-Verwaltung ist deaktiviert.
                Zum Ändern bitte zuerst reaktivieren.
              </p>
            )}
          </Card>
        </TabsContent>

        {/* Einsatzbereiche */}
        <TabsContent value="einsatzbereiche">
          <Card className="bg-white border border-slate-200 shadow-sm p-6 space-y-4">
            <div
              className="flex items-center gap-1.5 flex-wrap"
              data-testid="detail-eb-badges"
            >
              {ebs.length === 0 ? (
                <span className="text-xs text-amber-600 italic">
                  Kein Einsatzbereich
                </span>
              ) : (
                ebs.map((eb) => (
                  <Badge
                    key={eb.id}
                    variant="outline"
                    className={`text-xs ${eb.archived ? "bg-slate-100 border-slate-200 text-slate-500 line-through decoration-slate-300" : "bg-indigo-50 border-indigo-200 text-indigo-800"}`}
                  >
                    <MapPin className="w-3 h-3 mr-1" />
                    {eb.name}
                  </Badge>
                ))
              )}
            </div>
            <p className="text-xs text-slate-500">
              Einsatzbereiche steuern, welche Dokumentationen automatisch
              vorgeschlagen und zugewiesen werden. Änderung über „Bearbeiten&ldquo;.
            </p>
            <Button
              variant="outline"
              onClick={() => setEditOpen(true)}
              data-testid="detail-eb-edit"
              className="h-9"
            >
              <Pencil className="w-4 h-4 mr-1.5" /> Bearbeiten
            </Button>
          </Card>
        </TabsContent>

        {/* Unterweisungen — bestehende Funktionalität 1:1 (fünf klickbare
            Kategorien, öffnet den unveränderten EmployeeAssignmentsDrawer). */}
        <TabsContent value="unterweisungen">
          <Card className="bg-white border border-slate-200 shadow-sm p-6">
            <div
              className="grid grid-cols-2 sm:grid-cols-5 gap-3"
              data-testid="detail-unterweisungen-stats"
            >
              {STAT_META.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setDrawerState({ open: true, category: m.key })}
                  data-testid={`employee-stat-${m.testKey}-${employee.id}`}
                  className={`text-center px-2 py-3 rounded-md focus:outline-none focus:ring-2 transition-colors cursor-pointer ${m.hover}`}
                  title={m.title}
                >
                  <div
                    className={`font-heading text-2xl font-bold ${m.color(stats[m.key] || 0)}`}
                  >
                    {stats[m.key] || 0}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{m.label}</div>
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Führerschein — nur bei aktivem Modul (Block-H-Gate). Nur
            Fahrer-Stammdaten + kurzer Status; Freigabe-Workflow bleibt
            exklusiv auf der Führerschein-Seite. */}
        {fskModuleActive && (
          <TabsContent value="fuehrerschein">
            <Card className="bg-white border border-slate-200 shadow-sm p-6 space-y-4">
              {!employee.is_driver ? (
                <p
                  className="text-sm text-slate-600"
                  data-testid="detail-fsk-not-driver"
                >
                  Dieser Mitarbeiter ist nicht als Fahrer gekennzeichnet. Die
                  Fahrer-Zuweisung erfolgt über „Bearbeiten&ldquo;.
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className="text-xs bg-slate-900 border-slate-900 text-white"
                    >
                      <Car className="w-3 h-3 mr-1" />
                      Fahrer
                    </Badge>
                    {hasOpenFskRecord ? (
                      <Badge
                        variant="outline"
                        className="text-xs bg-amber-50 border-amber-200 text-amber-800"
                        data-testid="detail-fsk-status-open"
                      >
                        Prüfung offen
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-xs bg-emerald-50 border-emerald-200 text-emerald-800"
                        data-testid="detail-fsk-status-none"
                      >
                        Keine offene Prüfung
                      </Badge>
                    )}
                  </div>
                  <div
                    className="flex items-center gap-2 flex-wrap"
                    data-testid="detail-fsk-categories"
                  >
                    {(employee.driver_categories || []).length === 0 ? (
                      <span className="text-xs text-slate-500 italic">
                        Keine Fahrzeugarten zugewiesen.
                      </span>
                    ) : (
                      (employee.driver_categories || []).map((identifier) => {
                        const cat = vehicleCategories.find(
                          (c) => c.identifier === identifier
                        );
                        const src = cat
                          ? getVehiclePictogramSrc(cat.pictogram_key)
                          : null;
                        const label = cat ? cat.display_name : identifier;
                        return (
                          <span
                            key={identifier}
                            className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg bg-slate-100 border border-slate-200"
                            title={label}
                            data-testid={`detail-fsk-category-chip-${identifier}`}
                          >
                            {src ? (
                              <img
                                src={src}
                                alt=""
                                className="w-12 h-12 rounded-md object-contain bg-white p-0.5"
                              />
                            ) : (
                              <Car className="w-6 h-6 text-slate-500" />
                            )}
                            <span className="text-sm font-medium text-slate-800">
                              {label}
                            </span>
                          </span>
                        );
                      })
                    )}
                  </div>
                  <div
                    className="text-sm text-slate-600"
                    data-testid="detail-fsk-interval"
                  >
                    Führerschein-Wiedervorlage: alle{" "}
                    {Number(employee.driver_reverification_interval_months) || 6}{" "}
                    Monate
                  </div>
                </>
              )}
              <div className="pt-2 border-t border-slate-100 space-y-3">
                {/* UI-REDESIGN Ergänzung (Aug 2026, Punkt 6): letzter
                    abgeschlossener Führerschein-Record → identische Downloads
                    wie auf der Führerschein-Owner-Seite bei abgeschlossenem
                    Record (PDF-Nachweis + WORM-Rohdatei JSON). Wiederverwendung
                    bestehender Endpoints; kein Foto (BE-003 bleibt gelöscht). */}
                {latestDecidedRecord ? (
                  <div
                    data-testid="detail-fsk-latest-downloads"
                    className="flex flex-wrap items-center gap-3"
                  >
                    <button
                      type="button"
                      data-testid="detail-fsk-pdf-download"
                      onClick={async () => {
                        try {
                          const { blob, headers } =
                            await api.fetchFskNachweisPdfBlob(
                              latestDecidedRecord.id
                            );
                          saveBlobAs(
                            blob,
                            `${latestDecidedRecord.id}.pdf`,
                            headers
                          );
                          toast.success("PDF-Nachweis heruntergeladen.");
                        } catch (e) {
                          if (!handlePolicyError(e)) {
                            toast.error(
                              e?.response?.data?.detail || "Download fehlgeschlagen."
                            );
                          }
                        }
                      }}
                      className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
                    >
                      <FileText className="h-4 w-4" />
                      PDF herunterladen
                    </button>
                    <button
                      type="button"
                      data-testid="detail-fsk-worm-download"
                      onClick={async () => {
                        try {
                          const { blob, headers } =
                            await api.fetchFskWormJsonBlob(
                              latestDecidedRecord.id
                            );
                          saveBlobAs(
                            blob,
                            `${latestDecidedRecord.id}.json`,
                            headers
                          );
                          toast.success(
                            "WORM-Rohdatei (JSON) heruntergeladen."
                          );
                        } catch (e) {
                          if (!handlePolicyError(e)) {
                            toast.error(
                              e?.response?.data?.detail || "Download fehlgeschlagen."
                            );
                          }
                        }
                      }}
                      className="text-xs text-slate-500 underline decoration-dotted underline-offset-4 hover:text-slate-800"
                      title="Byte-identische WORM-Rohdatei (JSON) – für technische Nachvollziehbarkeit"
                    >
                      WORM-Rohdatei (JSON)
                    </button>
                    <span className="text-xs text-slate-500">
                      Letzte abgeschlossene Prüfung
                    </span>
                  </div>
                ) : (
                  <p
                    className="text-xs text-slate-500 italic"
                    data-testid="detail-fsk-no-decided"
                  >
                    Noch keine abgeschlossene Prüfung — PDF/WORM erscheinen
                    hier automatisch nach der ersten Entscheidung.
                  </p>
                )}
                <Link
                  to="/unternehmer/fuehrerschein"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900"
                  data-testid="detail-fsk-goto-page"
                >
                  <IdCard className="w-4 h-4" />
                  Prüfung und Freigabe auf der Führerschein-Seite
                  <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </div>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <EmployeeForm
        key={`edit-${employee.id}-${editOpen}`}
        open={editOpen}
        onOpenChange={setEditOpen}
        initial={employee}
        einsatzbereiche={einsatzbereiche}
        vehicleCategories={vehicleCategories}
        onSaved={reload}
      />

      <PinRevealDialog
        state={pinReveal}
        companyCode={session?.company_code}
        onOpenChange={(open) => setPinReveal((s) => ({ ...s, open }))}
      />

      <EmployeeAssignmentsDrawer
        open={drawerState.open}
        onOpenChange={(o) => setDrawerState((s) => ({ ...s, open: o }))}
        employee={employee}
        category={drawerState.category}
      />
    </div>
  );
}
